/**
 * TCGCSV catalog status — Pokémon + Lorcana in the dedicated `tcgcsv` database.
 *
 * Sibling of MtgjsonLoadStatus. Do not present these counts as MTGJSON UUID
 * coverage or AllPrintings progress.
 */

import React, { useEffect, useState } from 'react';
import { fetchLoadStatus, type TcgcsvCatalogStatus as TcgcsvStatus } from '../../api/mtgjson';
import { colors, spacing, typographyFlat } from '../../styles/tokens';
import { formatInstant } from '../../utils/datetime';

const emptyStatus = (): TcgcsvStatus => ({
  database: 'tcgcsv',
  service_name: 'tcgcsv_catalog',
  status: 'not_started',
  last_run_at: null,
  last_success_at: null,
  category_ids: [],
  group_count: null,
  product_count: null,
  price_count: null,
  price_snapshot_at: null,
  records_processed: 0,
  errors: 0,
  db_available: false,
  error: null,
});

export const TcgcsvCatalogStatus: React.FC = () => {
  const [status, setStatus] = useState<TcgcsvStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);
  const isInitialLoad = React.useRef(true);

  useEffect(() => {
    const loadStatus = async () => {
      try {
        if (isInitialLoad.current) {
          setLoading(true);
        }
        const loadData = await fetchLoadStatus();
        setStatus(loadData.tcgcsv ?? emptyStatus());
        setError(null);
      } catch (err) {
        console.error('Failed to load TCGCSV catalog status:', err);
        setError('Failed to load TCGCSV catalog status');
      } finally {
        if (isInitialLoad.current) {
          setLoading(false);
          isInitialLoad.current = false;
        }
      }
    };

    loadStatus();
    const interval = setInterval(() => {
      loadStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (value: string): string => {
    switch (value) {
      case 'success':
      case 'completed':
        return colors.success;
      case 'failed':
        return colors.error;
      case 'warning':
        return colors.warning;
      default:
        return colors.textSecondary;
    }
  };

  const getStatusLabel = (value: string): string => {
    switch (value) {
      case 'not_started':
        return 'Not Started';
      case 'success':
        return 'Success';
      case 'warning':
        return 'Warning';
      case 'failed':
        return 'Failed';
      case 'skipped':
        return 'Skipped';
      default:
        return value || 'Unknown';
    }
  };

  const styles: Record<string, React.CSSProperties> = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.md,
      padding: spacing.lg,
      backgroundColor: colors.backgroundAlt,
      border: `1px solid ${colors.border}`,
      borderRadius: '8px',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      userSelect: 'none',
    },
    title: {
      fontSize: typographyFlat.fontSize.md,
      fontWeight: 600,
      color: colors.text,
    },
    subtitle: {
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
      marginTop: spacing.xs,
    },
    overallStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.sm,
      fontSize: typographyFlat.fontSize.sm,
      fontWeight: 500,
    },
    statusIndicator: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
    },
    stats: {
      display: 'flex',
      gap: spacing.lg,
      flexWrap: 'wrap',
      fontSize: typographyFlat.fontSize.sm,
      color: colors.textSecondary,
    },
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: spacing.xs,
    },
    statLabel: {
      fontSize: typographyFlat.fontSize.xs,
      color: colors.textSecondary,
    },
    statValue: {
      fontSize: typographyFlat.fontSize.md,
      fontWeight: 600,
      color: colors.text,
    },
    error: {
      color: colors.error,
      fontSize: typographyFlat.fontSize.sm,
    },
    loading: {
      color: colors.textSecondary,
      fontSize: typographyFlat.fontSize.sm,
    },
  };

  if (loading && !status) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading TCGCSV catalog status...</div>
      </div>
    );
  }

  if (error && !status) {
    return (
      <div style={styles.container}>
        <div style={styles.error}>{error}</div>
      </div>
    );
  }

  const catalog = status ?? emptyStatus();

  return (
    <div style={styles.container}>
      <div style={styles.header} onClick={() => setExpanded(!expanded)}>
        <div>
          <div style={styles.title}>TCGCSV catalog</div>
          <div style={styles.subtitle}>
            Pokémon + Lorcana reference in database <code>tcgcsv</code> — not MTGJSON
          </div>
        </div>
        <div style={styles.overallStatus}>
          <div
            style={{
              ...styles.statusIndicator,
              backgroundColor: getStatusColor(catalog.status),
            }}
          />
          <span>{getStatusLabel(catalog.status)}</span>
          <span>{expanded ? '▼' : '▶'}</span>
        </div>
      </div>

      {expanded && (
        <>
          <div style={styles.stats}>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Database</div>
              <div style={styles.statValue}>{catalog.database}</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Last success</div>
              <div style={styles.statValue}>{formatInstant(catalog.last_success_at, 'Never')}</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Last run</div>
              <div style={styles.statValue}>{formatInstant(catalog.last_run_at, 'Never')}</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Category IDs</div>
              <div style={styles.statValue}>{catalog.category_ids.join(', ') || '—'}</div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Groups</div>
              <div style={styles.statValue}>
                {catalog.group_count == null ? 'n/a' : catalog.group_count.toLocaleString()}
              </div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Products</div>
              <div style={styles.statValue}>
                {catalog.product_count == null ? 'n/a' : catalog.product_count.toLocaleString()}
              </div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Price rows</div>
              <div style={styles.statValue}>
                {catalog.price_count == null ? 'n/a' : catalog.price_count.toLocaleString()}
              </div>
            </div>
            <div style={styles.statItem}>
              <div style={styles.statLabel}>Price snapshot</div>
              <div style={styles.statValue}>{formatInstant(catalog.price_snapshot_at, 'Unknown')}</div>
            </div>
          </div>
          {!catalog.db_available && (
            <div style={styles.error}>
              TCGCSV database unavailable
              {catalog.error ? `: ${catalog.error}` : ''}
            </div>
          )}
          {catalog.db_available && catalog.error && (
            <div style={styles.error}>{catalog.error}</div>
          )}
        </>
      )}
    </div>
  );
};
